/**
 * PlayerCard - Composite card showing a player: md Avatar (left), name + handle
 * text block (center), optional Badge or score (right). Surface mirrors the
 * Card `default` variant; Avatar and Badge are composed from the UI Kit.
 */

import { Avatar } from './Avatar.js';
import { Badge } from './Badge.js';
import { BaseUIComponent } from './BaseUIComponent.js';

import type { AvatarStatus } from './Avatar.js';
import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type PlayerCardStatus = AvatarStatus;

export interface PlayerCardData {
  /** Display name shown in bold on the first line. */
  name: string;
  /** Optional handle / subtitle shown muted on the second line. */
  handle?: string;
  /** Initials fallback for the avatar. */
  initials?: string;
  /** Optional avatar image URL (replaces initials). */
  imageUrl?: string;
  /** Optional status dot on the avatar. */
  status?: PlayerCardStatus;
  /** Optional numeric / text score shown on the right. */
  score?: string | number;
  /** Optional badge text shown on the right. */
  statusBadge?: string;
}

export interface PlayerCardOptions extends BaseUIComponentOptions, PlayerCardData {}

export class PlayerCard extends BaseUIComponent<PlayerCardOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Player Card',
    category: 'data',
    summary: 'Composite card with player avatar, name + handle, and optional badge or score.',
    useCases: ['player roster', 'lobby entry', 'leaderboard row', 'party member', 'friend chip'],
    optionsType: 'PlayerCardOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/player-card.html',
      styles: '@engine/assets/ui/styles/player-card.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private avatarSlot: HTMLElement | null = null;
  private avatar: Avatar | null = null;
  private nameElement: HTMLElement | null = null;
  private handleElement: HTMLElement | null = null;
  private scoreElement: HTMLElement | null = null;
  private badgeSlot: HTMLElement | null = null;
  private badge: Badge | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: PlayerCard.metadata.assetPaths.template,
      stylesPath: PlayerCard.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<PlayerCardOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      name: 'Player',
      handle: '',
      initials: '??',
      imageUrl: '',
      status: 'none',
      score: '',
      statusBadge: '',
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      name: this.options.name,
      handle: this.options.handle,
      score: this.options.score === '' ? '' : String(this.options.score),
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-player-card') as HTMLElement | null;
    this.avatarSlot = this.layout.querySelector('[data-player-card-avatar]') as HTMLElement | null;
    this.nameElement = this.layout.querySelector('[data-player-card-name]') as HTMLElement | null;
    this.handleElement = this.layout.querySelector('[data-player-card-handle]') as HTMLElement | null;
    this.scoreElement = this.layout.querySelector('[data-player-card-score]') as HTMLElement | null;
    this.badgeSlot = this.layout.querySelector('[data-player-card-badge]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    if (this.avatarSlot) {
      this.avatar = await this.mountChild(
        Avatar,
        {
          size: 'md',
          initials: this.options.initials,
          imageUrl: this.options.imageUrl,
          status: this.options.status,
        },
        this.avatarSlot,
      );
    }
    if (this.badgeSlot) {
      this.badge = await this.mountChild(
        Badge,
        { label: this.options.statusBadge, color: 'blue', size: 'medium' },
        this.badgeSlot,
      );
      this.applyBadgeVisibility();
    }
  }

  /** Replace the player data shown by the card. Unspecified fields are cleared. */
  public setPlayer(player: PlayerCardData): void {
    this.options.name = player.name;
    this.options.handle = player.handle ?? '';
    this.options.initials = player.initials ?? '??';
    this.options.imageUrl = player.imageUrl ?? '';
    this.options.status = player.status ?? 'none';
    this.options.score = player.score ?? '';
    this.options.statusBadge = player.statusBadge ?? '';

    if (this.nameElement) this.nameElement.textContent = this.options.name;
    if (this.handleElement) this.handleElement.textContent = this.options.handle;
    if (this.scoreElement) {
      this.scoreElement.textContent = this.options.score === '' ? '' : String(this.options.score);
    }
    this.avatar?.setInitials(this.options.initials);
    this.avatar?.setImageUrl(this.options.imageUrl);
    this.avatar?.setStatus(this.options.status);
    this.badge?.setLabel(this.options.statusBadge);
    this.applyBadgeVisibility();
  }

  private applyBadgeVisibility(): void {
    if (!this.badgeSlot) return;
    this.badgeSlot.style.display = this.options.statusBadge ? '' : 'none';
  }

  protected override onDestroy(): void {
    // avatar / badge are destroyed by BaseUIComponent.destroy() via mountChild tracking.
    this.avatar = null;
    this.badge = null;
    this.rootElement = null;
    this.avatarSlot = null;
    this.nameElement = null;
    this.handleElement = null;
    this.scoreElement = null;
    this.badgeSlot = null;
  }
}
